import React, {useCallback, useEffect, useState} from 'react'
import {DatasetInfo} from '@/generated/graphql'
import TableRow from '@mui/material/TableRow'
import TableBody from '@mui/material/TableBody'
import Box from '@mui/material/Box'
import MuiTableContainer from '@/components/common/MuiTableContainer'
import DatasetVisibilityIcon from '@/components/pages/DataInfoPage/dataset/DatasetVisibilityIcon'
import DeleteDatasetDialog from '@/components/pages/DataInfoPage/dataset/DeleteDatasetDialog'
import PrivatePublicDatasetDialog from '@/components/pages/DataInfoPage/dataset/PrivatePublicDatasetDialog'
import Checkbox from '@mui/material/Checkbox'
import DataInfoTableHead from '@/components/pages/DataInfoPage/table/DataInfoTableHead'
import Button from '@mui/material/Button'
import styled from 'styled-components'
import {LockPerson, Public, Delete} from '@mui/icons-material'
import {ThemeColorsHospitalic} from '@/assets/styles/color/ThemeColorsHospitalic'
import {SelectableDatasetInfo} from '@/components/pages/DataInfoPage/types'
import CollapsibleSection from '@/components/pages/DataInfoPage/table/CollapsibleSection'
import {useDispatch, useSelector} from 'react-redux'
import {SET_DATABASE_DETAIL, SET_DATASET_DETAIL} from '@/store/types'
import DatasetDetailDialog from '@/components/pages/DataInfoPage/dataset/DatasetDetailDialog'
import {InitialStateType} from '@/store/reducers'
import Link from '@mui/material/Link'
import { StyledTableCell } from '@/components/common/CustomStyledComponents'

const headers = ['Visible', 'ID', 'Name', 'Version', 'Parent Database ID', '']

const addSelectableToDatasets = (datasets?: DatasetInfo[]): SelectableDatasetInfo[] | undefined => {
    return datasets?.map(dataset => ({...dataset, selected: false}))
}

const StyledInlineEndDiv = styled.div`
    display: flex;
    align-items: center;
    justify-content: end;
    gap: 1.5em;
    padding-bottom: 0.5em;
`

const StyledActionButtonContent = styled.div`
    color: ${ThemeColorsHospitalic.PRIMARY_DARK} !important;
    display: flex;
    align-items: center;
    gap: 0.5em;
`

type DatasetTableProps = {
    datasets?: DatasetInfo[];
    maxHeight: string;
}

const DatasetTable = ({datasets, maxHeight}: DatasetTableProps) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
    const [publicPrivateDialogType, setPublicPrivateDialogType] = useState<'public' | 'private' | undefined>(undefined)
    const [selectableDatasets, setSelectabletableDatasets] = useState<SelectableDatasetInfo[] | undefined>(undefined)

    const dispatch = useDispatch()
    const {datasetIdDetail} = useSelector((state: InitialStateType) => state.dataInfo)
    const {type} = useSelector((state: InitialStateType) => state.user)

    const closeDeleteDialog = useCallback(() => {
        setDeleteDialogOpen(false)
    }, [])

    const closePublicPrivateDialog = useCallback(() => {
        setPublicPrivateDialogType(undefined)
    }, [])

    const onDatasetSelect = useCallback((datasetId: string) => {
        setSelectabletableDatasets((prev: any) => (prev.map((dataset: any) => dataset.dataset_id === datasetId ? {...dataset, selected: !dataset.selected} : dataset)))
    }, [])

    const onSelectAllDatasets = useCallback((allSelected: boolean) => {
        setSelectabletableDatasets((prev: any) => (prev.map((dataset: any) => ({...dataset, selected: allSelected}))))
    }, [])

    useEffect(() => {
        setSelectabletableDatasets(addSelectableToDatasets(datasets))
    }, [datasets])

    return (
        <CollapsibleSection header={'Datasets (' + (datasets?.length || 0) + ')'} defaultExpanded>
            <Box paddingTop={2}>
                {type === 'user' && (
                    <StyledInlineEndDiv>
                        <Button variant="outlined" onClick={() => setDeleteDialogOpen(true)}><StyledActionButtonContent><Delete/>DELETE</StyledActionButtonContent></Button>
                        <Button variant="outlined" onClick={() => setPublicPrivateDialogType('public')}><StyledActionButtonContent><Public/>MAKE PUBLIC</StyledActionButtonContent></Button>
                        <Button variant="outlined" onClick={() => setPublicPrivateDialogType('private')}><StyledActionButtonContent><LockPerson/>MAKE PRIVATE</StyledActionButtonContent></Button>
                    </StyledInlineEndDiv>
                )}
                <MuiTableContainer maxHeight={maxHeight} noData={!datasets || datasets.length <= 0} borderRadius='1em'>
                    <DataInfoTableHead headers={headers} onSelectAll={onSelectAllDatasets} allSelected={selectableDatasets?.every(dataset => dataset.selected)}/>
                    <TableBody>
                        {selectableDatasets?.map((dataset, idx) => (
                            <TableRow key={dataset.dataset_id + '-' + idx}>
                                <StyledTableCell padding="checkbox">
                                    <Checkbox
                                        color="primary"
                                        checked={dataset.selected}
                                        onClick={() => onDatasetSelect(dataset.dataset_id)}
                                    />
                                </StyledTableCell>
                                <StyledTableCell align='center' padding="checkbox">
                                    <DatasetVisibilityIcon isPublic={dataset.public}/>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Link color={ThemeColorsHospitalic.INFO_DARK} onClick={() => dispatch(SET_DATASET_DETAIL(dataset.dataset_id))}>
                                        {dataset.dataset_id}
                                    </Link>
                                </StyledTableCell>
                                <StyledTableCell>{dataset.dataset_name}</StyledTableCell>
                                <StyledTableCell>{dataset.version}</StyledTableCell>
                                <StyledTableCell>
                                    {type === 'user' && (
                                        <Link color={ThemeColorsHospitalic.INFO_DARK} onClick={() => dispatch(SET_DATABASE_DETAIL(dataset.parent_database_id))}>
                                            {dataset.parent_database_id}
                                        </Link>
                                    )}
                                    {type === 'guest' && dataset.parent_database_id}
                                </StyledTableCell>
                                <StyledTableCell align='right'>
                                    <Button variant='contained' size="small" onClick={() => dispatch(SET_DATASET_DETAIL(dataset.dataset_id))}>Detail</Button>
                                </StyledTableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </MuiTableContainer>
                <DeleteDatasetDialog datasets={selectableDatasets} open={deleteDialogOpen} onClose={closeDeleteDialog}/>
                <PrivatePublicDatasetDialog
                    datasets={selectableDatasets}
                    open={publicPrivateDialogType === 'private'}
                    type={'private'}
                    onClose={closePublicPrivateDialog}/>
                <PrivatePublicDatasetDialog
                    datasets={selectableDatasets}
                    open={publicPrivateDialogType === 'public'}
                    type={'public'}
                    onClose={closePublicPrivateDialog}/>
                {!!datasetIdDetail && <DatasetDetailDialog datasetId={datasetIdDetail}/>}
            </Box>
        </CollapsibleSection>
    )
}

export default DatasetTable